import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Chip,
  LinearProgress,
  Fade,
  Grow,
  useTheme,
  alpha,
  Button,
  Divider
} from '@mui/material';
import {
  PeopleAlt as PeopleIcon,
  Build as BuildIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Speed as SpeedIcon,
  ShoppingCart as ShoppingCartIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import { getAllCustomers } from '../../services/customerService';
import { getAllMaintenanceRecordsUnlimited } from '../../services/maintenanceService';
import { getAllInventoryItems } from '../../services/inventoryService';
import { getTodaySalesTotal } from '../../services/posService';
import { getAllOrders } from '../../services/storeService';
import { MaintenanceRecord, MaintenanceStatus } from '../../utils/types';
import { Order, OrderStatus, PaymentStatus as StorePaymentStatus } from '../../types/store';
import { useCurrency } from '../../contexts/CurrencyContext';
import QuickStats from './QuickStats';
import DashboardAlerts from './DashboardAlerts';
import DebtSummaryCard from './DebtSummaryCard';
import QuickDebtAlerts from './QuickDebtAlerts';

interface DashboardStats {
  totalCustomers: number;
  activeMaintenanceCount: number;
  todaySales: number;
  totalInventory: number;
  lowStockItems: number;
  completedToday: number;
  pendingOrders: number;
  monthlyRevenue: number;
}

interface OperationSummary {
  total: number;
  active: number;
  completed: number;
  pending: number;
  revenue: number;
  unpaid: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  link?: string;
  delay?: number;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  link,
  delay = 0
}) => {
  const theme = useTheme();

  return (
    <Grow in timeout={1000 + delay}>
      <Card
        sx={{
          height: '100%',
          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          borderRadius: 3,
          transition: 'all 0.3s ease',
          cursor: link ? 'pointer' : 'default',
          '&:hover': link ? {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 25px ${alpha(color, 0.3)}`,
            borderColor: alpha(color, 0.4)
          } : {},
          position: 'relative',
          overflow: 'hidden'
        }}
        component={link ? RouterLink : 'div'}
        to={link}
        style={{ textDecoration: 'none' }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `linear-gradient(45deg, ${alpha(color, 0.1)}, ${alpha(color, 0.05)})`,
            zIndex: 0
          }}
        />

        <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: color,
                width: 56,
                height: 56,
                boxShadow: `0 4px 14px ${alpha(color, 0.4)}`
              }}
            >
              {icon}
            </Avatar>

            {trend && (
              <Chip
                icon={trend.isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${trend.isPositive ? '+' : ''}${trend.value}%`}
                size="small"
                color={trend.isPositive ? 'success' : 'error'}
                variant="outlined"
              />
            )}
          </Box>

          <Typography variant="h3" fontWeight="bold" color="text.primary" gutterBottom>
            {typeof value === 'number' ? value.toLocaleString('ar-EG') : value}
          </Typography>

          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>

          {link && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, color }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                عرض التفاصيل
              </Typography>
              <ArrowForwardIcon fontSize="small" />
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );
};

const QuickActionCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  link: string;
  delay?: number;
}> = ({ title, description, icon, color, link, delay = 0 }) => {
  return (
    <Fade in timeout={1500 + delay}>
      <Card
        component={RouterLink}
        to={link}
        sx={{
          textDecoration: 'none',
          height: '100%',
          borderRadius: 3,
          transition: 'all 0.3s ease',
          border: `1px solid ${alpha(color, 0.2)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha(color, 0.25)}`,
            borderColor: alpha(color, 0.4)
          }
        }}
      >
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 48,
              height: 48,
              mx: 'auto',
              mb: 2
            }}
          >
            {icon}
          </Avatar>

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

const OperationCard: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  summary: OperationSummary;
  link: string;
  primaryLabel: string;
  secondaryLabel: string;
  delay?: number;
}> = ({ title, subtitle, icon, color, summary, link, primaryLabel, secondaryLabel, delay = 0 }) => {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const completionRate = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0;

  return (
    <Grow in timeout={900 + delay}>
      <Card
        component={RouterLink}
        to={link}
        sx={{
          height: '100%',
          textDecoration: 'none',
          borderRadius: 2,
          border: `1px solid ${alpha(color, 0.22)}`,
          background: theme.palette.background.paper,
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: alpha(color, 0.45)
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 48, height: 48 }}>
              {icon}
            </Avatar>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">{primaryLabel}</Typography>
              <Typography variant="h4" fontWeight={800}>{summary.active.toLocaleString('ar-EG')}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">{secondaryLabel}</Typography>
              <Typography variant="h4" fontWeight={800}>{summary.completed.toLocaleString('ar-EG')}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">إجمالي العمليات</Typography>
              <Typography variant="h6" fontWeight={700}>{summary.total.toLocaleString('ar-EG')}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">إيراد حقيقي</Typography>
              <Typography variant="h6" fontWeight={700}>{formatCurrency(summary.revenue)}</Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">معدل الإكمال</Typography>
              <Typography variant="body2" fontWeight={700}>{completionRate}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionRate}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: alpha(color, 0.12),
                '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 1 }
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`قيد الانتظار: ${summary.pending.toLocaleString('ar-EG')}`} size="small" />
            <Chip
              label={`غير محصل: ${formatCurrency(summary.unpaid)}`}
              size="small"
              color={summary.unpaid > 0 ? 'warning' : 'success'}
              variant="outlined"
            />
          </Box>
        </CardContent>
      </Card>
    </Grow>
  );
};

const ModernDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const { formatCurrency } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalCustomers: 0,
    activeMaintenanceCount: 0,
    todaySales: 0,
    totalInventory: 0,
    lowStockItems: 0,
    completedToday: 0,
    pendingOrders: 0,
    monthlyRevenue: 0
  });
  const [maintenanceSummary, setMaintenanceSummary] = useState<OperationSummary>({
    total: 0,
    active: 0,
    completed: 0,
    pending: 0,
    revenue: 0,
    unpaid: 0
  });
  const [storeSummary, setStoreSummary] = useState<OperationSummary>({
    total: 0,
    active: 0,
    completed: 0,
    pending: 0,
    revenue: 0,
    unpaid: 0
  });
  const [recentMaintenance, setRecentMaintenance] = useState<MaintenanceRecord[]>([]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [customers, maintenanceRecords, inventory, salesTotal, storeOrders] = await Promise.all([
        getAllCustomers(),
        getAllMaintenanceRecordsUnlimited(),
        getAllInventoryItems(),
        getTodaySalesTotal(),
        getAllOrders()
      ]);

      // Calculate stats
      const activeRecords = maintenanceRecords.filter(
        record => record.status === MaintenanceStatus.PENDING || record.status === MaintenanceStatus.IN_PROGRESS
      );

      const completedToday = maintenanceRecords.filter(
        record => record.status === MaintenanceStatus.COMPLETED &&
        record.completedAt &&
        new Date(record.completedAt).toDateString() === new Date().toDateString()
      ).length;

      const lowStockItems = inventory.filter(item => item.quantity <= 5).length;
      const deliveredMaintenance = maintenanceRecords.filter(record => record.status === MaintenanceStatus.DELIVERED);
      const maintenanceRevenue = maintenanceRecords.reduce(
        (sum, record) => sum + (record.totalPaid || record.paid || 0),
        0
      );
      const maintenanceUnpaid = maintenanceRecords.reduce(
        (sum, record) => sum + Math.max(0, record.remainingAmount || 0),
        0
      );
      const activeStoreOrders = (storeOrders as Order[]).filter(
        order => order.orderStatus !== OrderStatus.DELIVERED && order.orderStatus !== OrderStatus.CANCELLED
      );
      const deliveredStoreOrders = (storeOrders as Order[]).filter(order => order.orderStatus === OrderStatus.DELIVERED);
      const pendingStoreOrders = (storeOrders as Order[]).filter(order => order.orderStatus === OrderStatus.PENDING);
      const storeRevenue = (storeOrders as Order[])
        .filter(order => order.paymentStatus === StorePaymentStatus.PAID)
        .reduce((sum, order) => sum + (order.total || 0), 0);
      const storeUnpaid = (storeOrders as Order[])
        .filter(order => order.paymentStatus !== StorePaymentStatus.PAID && order.paymentStatus !== StorePaymentStatus.REFUNDED)
        .reduce((sum, order) => sum + (order.total || 0), 0);

      setStats({
        totalCustomers: customers.length,
        activeMaintenanceCount: activeRecords.length,
        todaySales: salesTotal,
        totalInventory: inventory.length,
        lowStockItems,
        completedToday,
        pendingOrders: activeStoreOrders.length,
        monthlyRevenue: storeRevenue + maintenanceRevenue
      });

      setMaintenanceSummary({
        total: maintenanceRecords.length,
        active: activeRecords.length,
        completed: deliveredMaintenance.length,
        pending: maintenanceRecords.filter(record => record.status === MaintenanceStatus.PENDING).length,
        revenue: maintenanceRevenue,
        unpaid: maintenanceUnpaid
      });

      setStoreSummary({
        total: (storeOrders as Order[]).length,
        active: activeStoreOrders.length,
        completed: deliveredStoreOrders.length,
        pending: pendingStoreOrders.length,
        revenue: storeRevenue,
        unpaid: storeUnpaid
      });

      setRecentMaintenance(maintenanceRecords);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('حدث خطأ أثناء تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'صباح الخير';
    if (hour < 17) return 'مساء الخير';
    return 'مساء الخير';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" gutterBottom>
              جاري تحميل لوحة التحكم...
            </Typography>
            <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Fade in timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {getGreeting()}، {currentUser?.name} 👋
              </Typography>
              <Typography variant="h6" color="text.secondary">
                إليك نظرة عامة على أداء نظامك اليوم
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDashboardData}
              sx={{ borderRadius: 2 }}
            >
              تحديث البيانات
            </Button>
          </Box>

          {error && (
            <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2 }}>
              <Typography>{error}</Typography>
            </Paper>
          )}
        </Box>
      </Fade>

      {/* Main Operation Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <OperationCard
            title="الصيانة"
            subtitle="طلبات الصيانة من بيانات النظام الفعلية"
            icon={<BuildIcon />}
            color={theme.palette.warning.main}
            summary={maintenanceSummary}
            link="/admin/maintenance-records"
            primaryLabel="قيد العمل"
            secondaryLabel="تم التسليم"
            delay={0}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <OperationCard
            title="المتجر الإلكتروني"
            subtitle="طلبات المتجر والتحصيل من بيانات الطلبات"
            icon={<ShoppingCartIcon />}
            color={theme.palette.success.main}
            summary={storeSummary}
            link="/admin/orders"
            primaryLabel="طلبات نشطة"
            secondaryLabel="تم التسليم"
            delay={100}
          />
        </Grid>
      </Grid>

      {false && (
      <>
      {/* Main Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="إجمالي العملاء"
            value={stats.totalCustomers}
            icon={<PeopleIcon />}
            color={theme.palette.primary.main}
            trend={{ value: 12, isPositive: true }}
            link="/admin/customers"
            delay={0}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="أجهزة قيد الصيانة"
            value={stats.activeMaintenanceCount}
            icon={<BuildIcon />}
            color={theme.palette.warning.main}
            trend={{ value: -5, isPositive: false }}
            link="/admin/maintenance-records"
            delay={100}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="مبيعات اليوم"
            value={formatCurrency(stats.todaySales)}
            icon={<MoneyIcon />}
            color={theme.palette.success.main}
            trend={{ value: 8, isPositive: true }}
            link="/admin/pos"
            delay={200}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="إجمالي المخزون"
            value={stats.totalInventory}
            icon={<InventoryIcon />}
            color={theme.palette.info.main}
            trend={{ value: 3, isPositive: true }}
            link="/admin/inventory"
            delay={300}
          />
        </Grid>
      </Grid>

      {/* Secondary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="مخزون منخفض"
            value={stats.lowStockItems}
            icon={<NotificationsIcon />}
            color={theme.palette.error.main}
            link="/admin/inventory"
            delay={400}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="مكتمل اليوم"
            value={stats.completedToday}
            icon={<AssessmentIcon />}
            color={theme.palette.success.main}
            link="/admin/maintenance-records"
            delay={500}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="طلبات معلقة"
            value={stats.pendingOrders}
            icon={<ShoppingCartIcon />}
            color={theme.palette.warning.main}
            link="/admin/orders"
            delay={600}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="أداء النظام"
            value="ممتاز"
            icon={<SpeedIcon />}
            color={theme.palette.primary.main}
            link="/admin/performance"
            delay={700}
          />
        </Grid>
      </Grid>

      {/* Quick Stats */}
      <QuickStats
        stats={{
          customers: stats.totalCustomers,
          maintenance: stats.activeMaintenanceCount,
          sales: stats.todaySales,
          inventory: stats.totalInventory,
          lowStock: stats.lowStockItems,
          completed: stats.completedToday
        }}
        trends={{
          customers: 12,
          maintenance: -5,
          sales: 8,
          inventory: 3
        }}
      />
      </>
      )}

      {/* Dashboard Alerts and Debt Summary */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid item xs={12} lg={8}>
          <DashboardAlerts
            lowStockCount={stats.lowStockItems}
            overdueMaintenanceCount={Math.floor(stats.activeMaintenanceCount * 0.3)}
            pendingOrdersCount={stats.pendingOrders}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <DebtSummaryCard elevation={3} />
        </Grid>
      </Grid>

      {/* Quick Debt Alerts */}
      <Box sx={{ mt: 3 }}>
        <QuickDebtAlerts maxItems={5} showExpanded={true} />
      </Box>

      {/* Quick Actions */}
      <Fade in timeout={1000}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, mt: 4 }}>
          إجراءات سريعة
        </Typography>
      </Fade>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <QuickActionCard
            title="إضافة عميل"
            description="إضافة عميل جديد للنظام"
            icon={<PeopleIcon />}
            color={theme.palette.primary.main}
            link="/admin/customers"
            delay={0}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <QuickActionCard
            title="صيانة جديدة"
            description="إنشاء طلب صيانة جديد"
            icon={<BuildIcon />}
            color={theme.palette.warning.main}
            link="/admin/maintenance/create"
            delay={100}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <QuickActionCard
            title="إضافة منتج"
            description="إضافة منتج جديد للمخزون"
            icon={<InventoryIcon />}
            color={theme.palette.info.main}
            link="/admin/inventory/add"
            delay={200}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <QuickActionCard
            title="نقطة البيع"
            description="فتح نقطة البيع"
            icon={<ShoppingCartIcon />}
            color={theme.palette.success.main}
            link="/admin/pos"
            delay={300}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <QuickActionCard
            title="التقارير"
            description="عرض التقارير والإحصائيات"
            icon={<AssessmentIcon />}
            color={theme.palette.secondary.main}
            link="/admin/reports"
            delay={400}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <QuickActionCard
            title="التقرير الشهري"
            description="تقرير المكسب والخسارة الشامل"
            icon={<AssessmentIcon />}
            color={theme.palette.info.main}
            link="/admin/monthly-report"
            delay={450}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <QuickActionCard
            title="الإعدادات"
            description="إدارة إعدادات النظام"
            icon={<SpeedIcon />}
            color={theme.palette.primary.main}
            link="/admin/settings"
            delay={500}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModernDashboard;
